import { useState } from "react";
import ComponentCard from "../../common/ComponentCard";
import Label from "../../form/Label";
import InputField from '../../form/input/InputField';
import Select from '../../form/Select';
import MultiSelectCheckbox from '../../form/MultiSelectCheckbox';
import Switch from "../../form/switch/Switch";
import Button from "../../ui/button/Button";
import Alert from "../../ui/alert/Alert";
import { useNavigate } from "react-router";
import axios from "axios";
import { authService } from "../../../services/authService";

interface TheMembersFormData {
    active: boolean;
    name: string;
    apiKey: string;
    productId: string;
    offerList: string[];
    externalDeliveryId: string;
}

interface NewTheMembersFormProps {
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
    companyId?: string;
}

export default function NewTheMembersForm({ onSuccess, onError, companyId: companyIdProp }: NewTheMembersFormProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<TheMembersFormData>({
        name: "",
        apiKey: "",
        productId: "",
        offerList: [],
        active: true,
        externalDeliveryId: "",
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");

    const [loading, setLoading] = useState(false);
    const [offers, setOffers] = useState<any[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [loadingOffers, setLoadingOffers] = useState(false);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [selectAllOffers, setSelectAllOffers] = useState(false);
    const [selectedOffers, setSelectedOffers] = useState<string[]>([]);
    const [apiKeyValidated, setApiKeyValidated] = useState(false);
    const [configUrl, setConfigUrl] = useState<string>("");

    const companyId = companyIdProp || authService.getCompanyId?.();

    // Valida a chave API da TheMembers
    const validateTheMembersApiKey = async (apiKey: string) => {
        setLoading(true);
        setApiError("");
        setApiSuccess("");
        setConfigUrl("");
        try {
            const resp = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/membership/validate`,
                {
                    provider: "themembers",
                    apiKey,
                },
                { headers: { 'Authorization': `Bearer ${authService.getToken()}` } }
            );

            if (resp.data && resp.data.success) {
                setApiKeyValidated(true);
                setApiSuccess("Chave validada com sucesso!");
                setApiError("");

                await fetchOffers();
                await fetchProducts();
            } else {
                // Captura mensagem de erro específica do backend
                const errorMessage = resp.data?.message || resp.data?.error || "Chave inválida.";
                setApiError(errorMessage);
                setApiSuccess("");
                setApiKeyValidated(false);
            }
        } catch (error: any) {
            // Captura erro de requisição HTTP e extrai mensagem específica
            let errorMessage = "Chave inválida ou erro na validação.";

            if (error.response?.data) {
                errorMessage = error.response.data.message || error.response.data.error || errorMessage;
            }

            setApiError(errorMessage);
            setApiSuccess("");
            setApiKeyValidated(false);
        } finally {
            setLoading(false);
        }
    };

    // Busca as ofertas da Empresa (após chave validada)
    const fetchOffers = async () => {
        setLoadingOffers(true);
        setOffers([]);
        try {
            const resp = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/company-offers/${companyId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );
            if (resp.data && resp.data.success && Array.isArray(resp.data.data)) {
                const mappedOffers = resp.data.data.map((offer: any) => ({
                    value: offer.id,
                    label: offer.name
                }));
                setOffers(mappedOffers);
            }
        } catch (error) {
            setOffers([]);
            setApiError("Erro ao buscar ofertas.");
        } finally {
            setLoadingOffers(false);
        }
    };

    // Busca as ofertas de um produto específico
    const fetchProductOffers = async (productId: string) => {
        setLoadingOffers(true);
        setOffers([]);
        try {
            const resp = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products/${productId}/offers-with-links`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );
            if (resp.data && resp.data.offers && Array.isArray(resp.data.offers)) {
                const mappedOffers = resp.data.offers.map((offer: any) => ({
                    value: offer.id,
                    label: offer.name
                }));
                setOffers(mappedOffers);
            }
        } catch (error) {
            setOffers([]);
            setApiError("Erro ao buscar ofertas do produto.");
        } finally {
            setLoadingOffers(false);
        }
    };

    // Busca os produtos da empresa (após chave validada)
    const fetchProducts = async () => {
        setLoadingProducts(true);
        setProducts([]);
        try {
            const resp = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );
            if (resp.data && resp.data.products && Array.isArray(resp.data.products)) {
                const mappedProducts = resp.data.products.map((product: any) => ({
                    value: product.id,
                    label: product.name
                }));
                setProducts(mappedProducts);
            }
        } catch (error) {
            setProducts([]);
            setApiError("Erro ao buscar produtos.");
        } finally {
            setLoadingProducts(false);
        }
    };

    // Ao alterar a chave API, reseta estados dependentes. Outros campos não devem resetar a validação.
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { id, value } = e.target;
        setErrors((prev) => ({ ...prev, [id]: "" }));
        setFormData((prev) => ({ ...prev, [id]: value }));

        if (id === 'apiKey') {
            setApiError("");
            setApiSuccess("");
            setApiKeyValidated(false);
            setProducts([]);
            setOffers([]);
        }
    };

    const handleSwitchChange = (id: string, checked: boolean) => {
        setFormData((prev) => ({ ...prev, [id]: checked }));
    };

    const handleOffersChange = (values: string[]) => {
        const isAllSelected = values.includes('all');
        setErrors((prev) => ({ ...prev, offerList: "" }));
        setApiError("");
        setApiSuccess("");
        if (isAllSelected) {
            setSelectAllOffers(true);
            setSelectedOffers(['all']);
            setFormData((prev) => ({
                ...prev,
                offerList: [], // Array vazio = qualquer oferta
            }));
        } else {
            setSelectAllOffers(false);
            setSelectedOffers(values);
            setFormData((prev) => ({
                ...prev,
                offerList: values, // Ofertas específicas selecionadas
            }));
        }
    };

    const handleProductChange = async (value: string) => {
        setFormData((prev) => ({ ...prev, productId: value }));
        await fetchProductOffers(value);
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.name) newErrors.name = "Nome da integração é obrigatório";
        if (!formData.apiKey) newErrors.apiKey = "Chave API é obrigatória";
        if (apiKeyValidated && !formData.productId) newErrors.productId = "Selecione um produto";
        if (apiKeyValidated && !selectAllOffers && !formData.offerList.length)
            newErrors.offerList = "Selecione ao menos uma oferta ou 'Qualquer oferta'";
        if (apiKeyValidated && !formData.externalDeliveryId)
            newErrors.externalDeliveryId = "Informe o ID do produto TheMembers";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);
        setApiError("");
        setApiSuccess("");
        try {
            const integrationData: any = {
                name: formData.name,
                provider: "themembers",
                apiKey: formData.apiKey,
                apiUrl: 'https://registration.themembers.dev.br/api',
                companyId,
                productId: formData.productId,
                offersList: formData.offerList,
                active: formData.active,
                webhookUrl: 'https://dummy.sellyo',
                configUrl: configUrl,
                externalDeliveryId: formData.externalDeliveryId,
            };

            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/membership`,
                integrationData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`,
                    },
                }
            );
            // Considera sucesso se a API retornar status 200 e um objeto com ID
            if (response.status === 200 && response.data && response.data.id) {
                setApiSuccess("Integração criada com sucesso!");
                if (onSuccess) onSuccess(response.data);
                setTimeout(() => navigate('/apps/themembers'), 1500);
            } else {
                setApiError(response.data?.error || "Erro ao criar integração");
                if (onError) onError(response.data?.error || "Erro desconhecido");
            }
        } catch (error: any) {
            setApiError(error.response?.data?.error || error.message || "Erro ao criar integração");
            if (onError) onError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleGoBack = () => navigate(`/apps/themembers`);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="col-span-1 lg:col-span-2">
                <ComponentCard title="Criar nova integração TheMembers">
                    {/* Mensagens de erro e sucesso */}
                    {apiError && (
                        <div className="mt-4">
                            <Alert
                                variant="error"
                                title="Erro na validação"
                                message={apiError}
                            />
                        </div>
                    )}

                    {apiSuccess && (
                        <div className="mt-4">
                            <Alert
                                variant="success"
                                title="Sucesso"
                                message={apiSuccess}
                            />
                        </div>
                    )}

                    <form onSubmit={(e) => { e.preventDefault(); if (apiKeyValidated && !apiError) handleSubmit(); }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Switch
                                label="Ativar integração"
                                defaultChecked={true}
                                onChange={(checked) => handleSwitchChange("active", checked)}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="name">Nome da integração *</Label>
                            <InputField
                                type="text"
                                id="name"
                                placeholder="Informe um nome para identificação"
                                value={formData.name}
                                onChange={handleInputChange}
                                error={!!errors.name}
                                hint={errors.name}
                                disabled={apiKeyValidated && !apiError} // Só desabilita se validado com sucesso (sem erro)
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="apiKey">Chave API *</Label>
                            <InputField
                                type="text"
                                id="apiKey"
                                placeholder="Sua chave API"
                                value={formData.apiKey}
                                onChange={handleInputChange}
                                error={!!errors.apiKey}
                                hint={errors.apiKey}
                                disabled={apiKeyValidated && !apiError} // Só desabilita se validado com sucesso (sem erro)
                            />
                        </div>

                        {/* Botão de validação sempre aparece quando não está validado OU quando há erro */}
                        {(!apiKeyValidated || apiError) && (
                            <div className="mt-6 flex justify-end gap-4.5">
                                <Button
                                    size="md"
                                    variant="primary"
                                    onClick={() => validateTheMembersApiKey(formData.apiKey)}
                                    disabled={loading || !formData.apiKey || !formData.name}
                                >
                                    {loading ? "Validando..." : "Validar chave API"}
                                </Button>
                            </div>
                        )}

                        {/* Segunda parte só se a chave foi validada E não tiver erro */}
                        {apiKeyValidated && !apiError && (
                            <>
                                <div className="mt-5">
                                    <Label>Produto *</Label>
                                    <Select
                                        options={products}
                                        placeholder="Selecione um produto"
                                        onChange={handleProductChange}
                                        value={formData.productId}
                                        className={errors.productId ? "border-red-500" : ""}
                                    />
                                    {errors.productId && <p className="mt-1 text-sm text-red-500">{errors.productId}</p>}
                                    {loadingProducts && (
                                        <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                            <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                            Carregando produtos...
                                        </div>
                                    )}
                                </div>

                                <div className="mt-5">
                                    <Label>Ofertas *</Label>
                                    {!formData.productId ? (
                                        <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                Selecione um produto primeiro
                                            </p>
                                        </div>
                                    ) : loadingOffers ? (
                                        <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                            <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                            Carregando ofertas...
                                        </div>
                                    ) : (
                                        <div className="mt-2">
                                            <MultiSelectCheckbox
                                                options={[
                                                    {
                                                        id: 'all',
                                                        label: 'Qualquer oferta',
                                                        disabled: false,
                                                    },
                                                    ...offers.map((offer) => ({
                                                        id: offer.value,
                                                        label: `${offer.label}`,
                                                        disabled: false,
                                                        meta: offer,
                                                    })),
                                                ]}
                                                selectedValues={selectAllOffers ? ['all'] : selectedOffers}
                                                onChange={handleOffersChange}
                                                placeholder="Selecione a oferta"
                                                error={!!errors.offerList}
                                                hint={errors.offerList}
                                                searchPlaceholder="Pesquisar ofertas"
                                                showSelectAll={false}
                                            />
                                            {selectAllOffers && (
                                                <p className="mt-1 text-xs text-blue-500">
                                                    Esta integração será válida para todas as ofertas, incluindo futuras.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-5">
                                    <Label htmlFor="externalDeliveryId">ID do produto TheMembers *</Label>
                                    <InputField
                                        type="text"
                                        id="externalDeliveryId"
                                        placeholder="Informe o ID do produto na TheMembers"
                                        value={formData.externalDeliveryId}
                                        onChange={handleInputChange}
                                        error={!!errors.externalDeliveryId}
                                        hint={errors.externalDeliveryId}
                                    />
                                </div>

                                <div className="mt-6 flex justify-end gap-4.5">
                                    <Button
                                        size="md"
                                        variant="outline"
                                        onClick={handleGoBack}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        size="md"
                                        variant="primary"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                    >
                                        {loading ? "Salvando..." : "Criar integração"}
                                    </Button>
                                </div>
                            </>
                        )}
                    </form>
                </ComponentCard>
            </div>
        </div>
    );
}
